import {
  Box,
  Button,
  HStack,
  Image,
  Text,
  VStack,
} from "@chakra-ui/react";
import type { BudgetData } from "./GuidedBudgetEditor";
import moneyIcon from "../assets/reviewIcons/money.svg";
import { ScheduledBudgetPhaseRows } from "./ScheduledBudgetPhaseRows";

export type ReviewEditorSection =
  | "general"
  | "budget"
  | "audience"
  | "creative";

const REVIEW_ROW_ICON_SIZE = 6;
const REVIEW_ROW_LABEL_MIN_W = "120px";

export type ReviewDetailRow =
  | {
      kind: "simple";
      iconSrc: string;
      label: string;
      value: string;
      edit: boolean;
      section?: ReviewEditorSection;
    }
  | { kind: "scheduled_budget" };

function scheduledBudgetTotalDisplay(budget: BudgetData): string {
  const segs = budget.segments ?? [];
  const total = segs.reduce((sum, s) => {
    const raw = s.amount?.amount;
    const n = raw != null ? Number(raw) : NaN;
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const currency = segs[0]?.amount?.currency ?? "USD";
  const symbol = currency === "USD" ? "$" : `${currency} `;
  if (total <= 0 || !segs.length) return "—";
  return `${symbol}${Math.round(total)} Scheduled Budget`;
}

export type CreateCampaignReviewDetailRowProps = {
  row: ReviewDetailRow;
  budget: BudgetData;
  scheduleEventTimezone: string;
  onBackToEditor: (section?: ReviewEditorSection) => void;
  launching: boolean;
};

export function CreateCampaignReviewDetailRow({
  row,
  budget,
  scheduleEventTimezone,
  onBackToEditor,
  launching,
}: CreateCampaignReviewDetailRowProps) {
  if (row.kind === "scheduled_budget") {
    const segments = budget.segments ?? [];
    return (
      <Box borderTopWidth="1px" borderColor="gray.200" pt={3}>
        <VStack align="stretch" spacing={1.5}>
          <HStack
            align="center"
            w="full"
            spacing={1}
            justify="space-between"
          >
            <HStack
              minW={REVIEW_ROW_LABEL_MIN_W}
              spacing={1.5}
              align="center"
              flexShrink={0}
            >
              <Image
                src={moneyIcon}
                alt=""
                boxSize={REVIEW_ROW_ICON_SIZE}
                flexShrink={0}
              />
              <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                Budget
              </Text>
            </HStack>
            <Text
              flex={1}
              fontSize="sm"
              fontWeight="medium"
              color="gray.600"
              minW={0}
              noOfLines={2}
            >
              {scheduledBudgetTotalDisplay(budget)}
            </Text>
            <Button
              variant="link"
              fontSize="sm"
              fontWeight="normal"
              color="blue.600"
              flexShrink={0}
              onClick={() => onBackToEditor("budget")}
              cursor="pointer"
              isDisabled={launching}
              textDecoration="underline"
              _hover={{ textDecoration: "underline" }}
            >
              Edit
            </Button>
          </HStack>
          <VStack align="stretch" spacing={1.5}>
            <ScheduledBudgetPhaseRows
              segments={segments}
              eventTimezone={scheduleEventTimezone}
              labelMinW={REVIEW_ROW_LABEL_MIN_W}
            />
          </VStack>
        </VStack>
      </Box>
    );
  }

  const { iconSrc, label, value, edit, section } = row;
  return (
    <HStack
      align="center"
      w="full"
      spacing={1}
      borderTopWidth="1px"
      borderColor="gray.200"
      pt={3}
    >
      <HStack
        minW={REVIEW_ROW_LABEL_MIN_W}
        spacing={1.5}
        align="center"
        flexShrink={0}
      >
        <Image src={iconSrc} alt="" boxSize={REVIEW_ROW_ICON_SIZE} flexShrink={0} />
        {label.trim() ? (
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            {label}
          </Text>
        ) : null}
      </HStack>
      <Text flex={1} minW={0} fontSize="sm" fontWeight="medium" color="gray.600">
        {value}
      </Text>
      {edit && section ? (
        <Button
          variant="link"
          fontSize="sm"
          fontWeight="normal"
          color="blue.600"
          flexShrink={0}
          onClick={() => onBackToEditor(section)}
          cursor="pointer"
          isDisabled={launching}
          textDecoration="underline"
          _hover={{ textDecoration: "underline" }}
        >
          Edit
        </Button>
      ) : null}
    </HStack>
  );
}

export function reviewDetailRowKey(row: ReviewDetailRow): string {
  if (row.kind === "scheduled_budget") return "scheduled-budget";
  if (row.label.trim()) return row.label;
  return `review-row-${row.iconSrc}`;
}
